import { Component, inject, Input, OnInit, OnDestroy, ViewEncapsulation, ViewChild, ChangeDetectorRef, model } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule, formatDate } from '@angular/common';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule, MatCalendarCellClassFunction } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { startOfMonth, endOfMonth, addMinutes, isBefore, format, isValid, isSameDay, startOfDay, addMonths, isEqual, isAfter, parse } from 'date-fns';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { AppStateService } from '../../app-state.service';
import { AppointmentService } from '../appointment.service';
import { Appointment, AppointmentType, AppointmentTypeRequest, CreateAppointmentRequest, OperatoriesResponse, SlotsRequest, SlotsResponse } from '../appointment.model';
import { RegistrationPromptComponent } from '../registration-prompt/registration-prompt.component';
import { ConsentPromptComponent } from '../consent-prompt/consent-prompt.component';
import { takeUntil, Subject, Observable, map, startWith } from 'rxjs';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { FormConfigService } from '../forms-config.service';
import { SlotCacheService } from '../../slot-cache.service';
import { CalendarEventService } from '../../calender-event.service';
import { CustomCalendarHeaderComponent } from '../../calender-header.component';

interface TreatmentPlan {
  treatmentPlanId: number;
  patientNumber: number;
  dateTP: string;
  heading: string;
  note: string;
  responsParty: number;
  docNum: number;
  status: string;
  secUserNumEntry: number;
  secDateEntry: string;
  secDateTEdit: string;
  userNumPresenter: number;
  type: string;
  dateSigned: string;
  datePracticeSigned: string;
  signatureText: string;
  signaturePracticeText: string;
}
interface Procedure {
  patientNumber: number;
  procedureNumber: number;
  procedureCode: string;
  procedureDescription: string;
  priority: string;
  procedureStatus: string;
  procedureType: string;
  procedureCategory: string;
  procedureDate: string;
  toothNum: string;
  toothRange: string;
  procedureFee: string;
  surfaces: string;
  allowed: number;
  discount: number;
  primaryInsAmount: number;
  secondaryInsAmount: number;
  patientPortion: number;
}
@Component({
  selector: 'app-appointment-scheduler',
  standalone: true,
  templateUrl: './appointment-scheduler.component.html',
  styleUrls: ['./appointment-scheduler.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatSnackBarModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatSelectModule,
    MatAutocompleteModule,
    CustomCalendarHeaderComponent
  ]
})
export class AppointmentSchedulerComponent implements OnInit, OnDestroy {
  @Input() appointmentId?: any;
  clientId: string = '';
  appointmentForm!: FormGroup;
  isLoading = false; // Single loading state
  isGlobalLoading = false;
  availableSlots: any[] = [];
  displayedSlots: any = {};
  selectedSlot: any | null = null;
  currentAppointmentDetails: any | null = null;
  operatories: any[] = [];
  appointmentDuration = 30; // default appointment duration in minutes
  appState = inject(AppStateService);
  appointmentService = inject(AppointmentService);
  slotCacheService = inject(SlotCacheService);
  calendarEventService = inject(CalendarEventService);
  private unsubscribe$ = new Subject<void>();
  isDataReady = false;
  isScheduling = false;
  userFriendlyDate: string | null = null;
  loadingStatus = 'Loading...';
  isDatepickerVisible = false;
  isAppointmentTypesLoading = false;
  isOperatoriesLoading = false;
  isSlotsLoading = false;
  isSubmitting = false;
  isDatepickerLoading = false; // New state for date picker loading
  // New properties for date range restriction
  minDate: Date = new Date();
  maxDate: Date = addMonths(new Date(), 6); // Allow up to 6 months in the future
  selected = model<Date | null>(null);
  appointmentData: any;
  filteredTreatments!: Observable<string[]>;
  treatments: string[] = [
    'Chipped tooth',
    'Toothache',
    'Knocked-out tooth',
    'Lost filling or crown',
    'Abscess',
    'Teeth cleaning',
    'Cavity filling',
    'Root canal',
    'Teeth whitening',
    'Gum treatment',
    'Wisdom teeth removal'
  ];
  // Add a new property for treatment plans
  patientId: string = '';
  activeTreatmentPlan: TreatmentPlan | null = null;
  treatmentProcedures: Procedure[] = [];
  hasTreatmentPlans: boolean = false;
  selectedTreatmentPlan: TreatmentPlan | null = null;
  isTreatmentPlanLoading: boolean = false; // Loading state for treatment plans
  // Inject the FormConfigService
  formConfigService = inject(FormConfigService);

  showSuggestions = false;
  // Set to TRUE for authenticated users to describe their treatment needs
  showTreatmentInput = true;
  // Flag to indicate if showing confirmation after booking
  isConfirmationView = false;
  calendarLoadingMonths: { [key: string]: boolean } = {};
  currentViewedMonth: string = this.formatMonthKey(new Date());
  customCalendarHeader = CustomCalendarHeaderComponent;
  @ViewChild('calendar') calendar: any;
  //cdr = inject(ChangeDetectorRef);
  selectedDate: Date | null = null;

  constructor(
    private fb: FormBuilder,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef, // Inject ChangeDetectorRef
    private dialog: MatDialog
  ) {
    this.clientId = this.appState.getState().clientId || '';
    this.route.params.pipe(takeUntil(this.unsubscribe$)).subscribe(params => {
      this.appointmentId = params['appointmentId'] !== 'new' ? params['appointmentId'] : undefined;
    });
  }

  ngOnInit(): void {
    this.patientId = this.appState.getState().patientId || '';
    if (this.patientId) {
      this.checkForExistingTreatmentPlans();
    }
    this.slotCacheService.clearCache();
    this.initializeForm();
    this.setupFilteredTreatments();
    this.setupMonthSelectedSubscription();

    if (this.appointmentId && this.appointmentId !== '' && this.appointmentId !== 'new') {
      this.loadAppointment(this.appointmentId);
    }

    const appointmentData = this.appState.getState().appointmentData;
    const appointmentBooked = this.appState.getState().appointmentBooked;


    if (appointmentData) {
      const appointmentDate = parse(appointmentData.appointmentDateTime, 'yyyy-MM-dd HH:mm:ss', new Date());
      this.appointmentData = appointmentData;

      // Check if this is a confirmation view (appointment already booked)
      if (appointmentBooked) {
        this.isConfirmationView = true;
      } else {
        // This is rescheduling/editing, so populate form
        this.appointmentForm.patchValue({
          appointmentDescription: appointmentData.summary,
          appointmentType: appointmentData.appointmentTypeNumber,
          date: appointmentDate,
          time: format(appointmentDate, 'HH:mm'),
          operatoryNumber: appointmentData.operatoryNumber,
          lengthInMinutes: appointmentData.lengthInMinutes
        });
        this.selectedSlot = {
          start: appointmentDate,
          end: addMinutes(appointmentDate, appointmentData.lengthInMinutes),
          operatory: appointmentData.operatoryNumber
        };
      }
    }
  }

  ngOnDestroy(): void {
    this.slotCacheService.clearCache();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private initializeForm() {
    this.appointmentForm = this.fb.group({
      date: ['', Validators.required],
      time: ['', Validators.required],
      appointmentDescription: [''],
      appointmentType: ['', Validators.required],
      operatoryNumber: ['', Validators.required]
    });
  }

  private checkForExistingTreatmentPlans() {
    this.isTreatmentPlanLoading = true;
    this.formConfigService.fetchTreatmentPlans(this.clientId, this.patientId).subscribe({
      next: (data: any) => {
        if (data.active_treatment_plan && data.procedures.length > 0) {
          this.activeTreatmentPlan = data.active_treatment_plan;
          this.treatmentProcedures = data.procedures;
          this.hasTreatmentPlans = true;
          // Conditionally add "Based on the treatment plan" to treatments if a treatment plan exists
          if (this.activeTreatmentPlan) {
            this.treatments = ['Based on the active treatment plan', ...this.treatments];
            this.setupFilteredTreatments(); // Reinitialize filteredTreatments
            this.cdr.markForCheck(); // Trigger change detection
          }
        }
        this.isTreatmentPlanLoading = false;
      },
      error: (err: any) => {
        this.hasTreatmentPlans = false;
        this.isTreatmentPlanLoading = false;
      }
    });
  }

  selectTreatmentPlan(plan: TreatmentPlan) {
    this.selectedTreatmentPlan = plan;
    this.appointmentForm.patchValue({
      appointmentDescription: plan.heading
    });
    // Note: appointmentType should be set after API call to getAppointmentTypes
  }

  clearSelectedTreatmentPlan() {
    this.selectedTreatmentPlan = null;
    this.appointmentForm.reset();
  }

  private setupFilteredTreatments() {
    this.filteredTreatments = this.appointmentForm.get('appointmentDescription')!.valueChanges.pipe(
      startWith(''),
      map(value => this._filter(value || ''))
    );
  }

  private setupMonthSelectedSubscription() {
    this.calendarEventService.monthSelected$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(date => this.onMonthSelected(date));
  }

  private _filter(value: string): string[] {
    const filterValue = value.toLowerCase();
    return this.treatments.filter(option => option.toLowerCase().includes(filterValue));
  }

  hideSuggestions() {
    setTimeout(() => {
      this.showSuggestions = false;
    }, 200);  // Delay hiding to allow click event to register
  }

  loadAppointment(id: number) {
    this.isLoading = true;
    this.loadingStatus = 'Loading appointment details...';
    this.appointmentService.fetchAppointmentInfo(id)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (appointment: Appointment) => {
          this.isLoading = false;
          if (appointment) {
            this.currentAppointmentDetails = appointment;
            const appointmentDate = parse(appointment.appointmentDateTime, 'yyyy-MM-dd HH:mm:ss', new Date());
            if (isValid(appointmentDate)) {
              this.appointmentForm.patchValue({
                date: appointmentDate,
                time: format(appointmentDate, 'HH:mm'),
                appointmentDescription: appointment.note
              });
              const appointmentType = appointment.appointmentType;
              if (!appointmentType || appointmentType === '') {
                this.snackBar.open('Unable to find appointment type .. please contact Clinic Support', 'Close', { duration: 3000 });
                return;
              }
              this.appointmentForm.patchValue({ appointmentType: appointmentType });
              this.loadAvailableOperatories(appointmentType);
            } else {
              this.snackBar.open('Invalid appointment date received.', 'Close', { duration: 3000 });
            }
          }
        },
        error: () => {
          this.isLoading = false;
          this.snackBar.open('Error fetching appointment details. Please try again.', 'Close', { duration: 3000 });
        }
      });
  }

  onEnter(event: any) {
    if (event) {
      event.preventDefault();
    }
    this.onAppointmentDescriptionSubmit();
  }

  onAppointmentDescriptionSubmit() {
    const description = this.appointmentForm.get('appointmentDescription')?.value;
    if (description) {
      this.isDataReady = false;
      this.isLoading = true;
      this.loadingStatus = 'Loading appointment types...';
      this.isAppointmentTypesLoading = true;
  
      // Initialize the request with procedureCode as null by default
      // Add "Existing Patient" prefix for better appointment type matching (this component is only for existing patients)
      const prefixedDescription = `Existing Patient ${description}`;
      const appointmentRequest: AppointmentTypeRequest = {
        treatmentRequest: prefixedDescription,
        procedureCode:  description === 'Based on the active treatment plan' && this.treatmentProcedures.length > 0 ? this.treatmentProcedures[0].procedureCode : null,
        isPlannedTreatment: description === 'Based on the active treatment plan' && this.treatmentProcedures.length > 0
      };      
  
      this.appointmentService.getAppointmentTypes(appointmentRequest)
        .subscribe({
          next: (response: AppointmentType[]) => {
            this.isAppointmentTypesLoading = false;
            this.isLoading = false;
            if (response.length === 0) {
              this.snackBar.open('No matching appointment type found.', 'Close', { duration: 5000 });
              return;
            }

            // Check if we got a single matched type or multiple types (no match)
            // When backend finds a match, it returns a single object, not an array
            // When no match, it returns all types as an array
            let appointmentType: AppointmentType;
            if (response.length === 1) {
              // Single match found
              appointmentType = response[0];
            } else if (response.length > 1 && !Array.isArray(response[0])) {
              // Multiple types returned - no direct match found
              // Use the first one but warn the user
              appointmentType = response[0];
              this.snackBar.open(
                `No exact match for "${description}". Using "${appointmentType.appointmentTypeName}" (${appointmentType.lengthInMinutes} min). You can describe your needs differently for a better match.`,
                'Close',
                { duration: 8000 }
              );
            } else {
              appointmentType = response[0];
            }

            if (appointmentType) {
              this.appointmentDuration = appointmentType.lengthInMinutes;
              this.appointmentForm.patchValue({ appointmentType: appointmentType.appointmentTypeNumber });
              this.loadAvailableOperatories(appointmentType.appointmentTypeName);
            } else {
              this.snackBar.open('No matching appointment type found. Please try a different description.', 'Close', { duration: 5000 });
            }
          },
          error: () => {
            this.isAppointmentTypesLoading = false;
            this.isLoading = false;
            this.snackBar.open('Error fetching appointment type. Please try again.', 'Close', { duration: 5000 });
          }
        });
    }
  }
     

  loadAvailableOperatories(appointmentTypeName: string) {
    this.isLoading = true;
    this.loadingStatus = 'Loading available operatories...';
    this.isOperatoriesLoading = true;
    this.appointmentService.getMatchingOperatories({ appointmentTypeName })
    .subscribe(
      {
        next: (response: OperatoriesResponse) => {
          this.isOperatoriesLoading = false;
          this.operatories = response.operatories;
          if (this.operatories.length === 0) {
            this.snackBar.open('No operatories available for the selected appointment type', 'Close', { duration: 5000 });
            this.isLoading = false;
          } else {
            const startDate = new Date();
            const operatories = this.operatories.map(operatory => operatory.operatoryNumber).join(',');
            this.loadAvailableSlots(startDate, operatories);
          }
        },
        error: () => {
          this.isOperatoriesLoading = false;
          this.isLoading = false;
          this.snackBar.open('Error fetching operatories. Please try again.', 'Close', { duration: 5000 });
        }
      });
  }

  loadAvailableSlots(startDate: Date, operatories: string) {
    const monthKey = format(startDate, 'yyyy-MM');
    const cachedSlots = this.slotCacheService.getCachedSlots(monthKey, operatories);
    if (cachedSlots) {
      this.availableSlots = cachedSlots;
      this.isDataReady = true;
      this.displayFirstAvailableDaySlots();
      this.isDatepickerVisible = true;
      this.isLoading = false;
      return;
    }

    const formattedStartDate = format(startDate, 'yyyy-MM-dd');
    const endDate = endOfMonth(startDate);
    const formattedEndDate = format(endDate, 'yyyy-MM-dd');

    let request: SlotsRequest = {
      startDate: formattedStartDate,
      endDate: formattedEndDate,
      operatories,
      lengthInMinutes: this.appointmentDuration
    };

    this.isLoading = true;
    this.loadingStatus = 'Loading available slots...';
    this.isSlotsLoading = true;
    this.calendarLoadingMonths[monthKey] = true;

    this.appointmentService.getAvailableSlots(request)
    .subscribe(
      {
        next: (response: SlotsResponse) => {
          this.isSlotsLoading = false;
          this.calendarLoadingMonths[monthKey] = false;
          let slots = response.slots.map(slot => ({
            ...slot,
            date: this.adjustDateForTimezone(new Date(slot.date + 'T00:00:00')),
            start: new Date(slot.start),
            end: new Date(slot.end)
          }));

          // filter based on appointment duration
          slots = slots.filter(slot => slot.duration >= this.appointmentDuration);

          this.availableSlots = slots;
          this.slotCacheService.setCachedSlots(monthKey, operatories, slots);
          this.isDataReady = true;
          this.displayFirstAvailableDaySlots();
          this.isDatepickerVisible = true;
          this.isLoading = false;
        },
        error: () => {
          this.isSlotsLoading = false;
          this.calendarLoadingMonths[monthKey] = false;
          this.isLoading = false;
          if (monthKey === this.currentViewedMonth) {
            this.availableSlots = [];
            this.displayFirstAvailableDaySlots();
            if (this.calendar) {
              this.calendar.updateTodaysDate();
            }
          }
          this.snackBar.open('Error fetching available slots. Please try again.', 'Close', { duration: 3000 });
        }
      });
  }

  prefetchNextMonthsSlots(currentStartDate: Date, operatories: string) {
    const nextMonth = addMonths(currentStartDate, 1);
    const monthAfterNext = addMonths(currentStartDate, 2);

    this.isDatepickerLoading = true;
    this.loadAvailableSlotsForMonth(nextMonth, 'nextMonth', operatories);
    this.loadAvailableSlotsForMonth(monthAfterNext, 'monthAfterNext', operatories);
  }

  loadAvailableSlotsForMonth(monthStart: Date, monthKey: string, operatories: string) {
    const cachedSlots = this.slotCacheService.getCachedSlots(monthKey, operatories);
    if (cachedSlots) {
      if (monthKey === this.currentViewedMonth) {
        this.availableSlots = cachedSlots;
        this.displayFirstAvailableDaySlots();
        if (this.calendar) {
          this.calendar.updateTodaysDate();
        }
      }
      return; // Skip loading if already cached
    }

    const startDate = startOfMonth(monthStart);
    const endDate = endOfMonth(monthStart);

    const requestPayload: SlotsRequest = {
      startDate: format(startDate, 'yyyy-MM-dd'),
      endDate: format(endDate, 'yyyy-MM-dd'),
      operatories,
      lengthInMinutes: this.appointmentDuration
    };

    this.calendarLoadingMonths[monthKey] = true;

    this.appointmentService.getAvailableSlots(requestPayload)
    .subscribe(
      {
        next: (response: SlotsResponse) => {
          let slots = response.slots.map(slot => ({
            ...slot,
            date: this.adjustDateForTimezone(new Date(slot.date + 'T00:00:00')),
            start: new Date(slot.start),
            end: new Date(slot.end)
          }));

          slots = slots.filter(slot => slot.duration >= this.appointmentDuration);

          this.slotCacheService.setCachedSlots(monthKey, operatories, slots);
          this.calendarLoadingMonths[monthKey] = false;
          this.isDatepickerLoading = false;
          if (monthKey === this.currentViewedMonth) {
            this.availableSlots = slots;
            this.displayFirstAvailableDaySlots();
            if (this.calendar) {
              this.calendar.updateTodaysDate();
            }
          }
        },
        error: () => {
          this.snackBar.open('Error fetching available slots. Please try again.', 'Close', { duration: 3000 });
          this.calendarLoadingMonths[monthKey] = false;
          this.isDatepickerLoading = false;
          if (monthKey === this.currentViewedMonth) {
            this.availableSlots = [];
            this.displayFirstAvailableDaySlots();
            if (this.calendar) {
              this.calendar.updateTodaysDate();
            }
          }
        }
      });
  }

  displayFirstAvailableDaySlots() {
    if (this.availableSlots.length > 0) {
      const firstAvailableDay = this.availableSlots[0].date;
      if (isValid(firstAvailableDay)) {
        this.selectedDate = firstAvailableDay;
        this.appointmentForm.patchValue({ date: firstAvailableDay });
        this.updateDisplayedSlots(firstAvailableDay);
      }
    }
  }

  adjustDateForTimezone(date: Date): Date {
    // Fix: Don't adjust timezone offset which was causing dates to shift to past
    // Just return the date as-is for local date operations
    return new Date(date);
  }

  updateDisplayedSlots(date: Date) {
    if (isValid(date)) {
      const selectedDate = startOfDay(date);
      this.selectedSlot = null;
      this.userFriendlyDate = format(selectedDate, 'MMMM d, yyyy');
      this.appointmentForm.patchValue({ time: '' });

      const slotsOnSelectedDate = this.availableSlots.filter(slot => {
        const slotDate = startOfDay(slot.date); // slot.date is already a Date object after transformation
        return isSameDay(slotDate, selectedDate);
      });

      this.displayedSlots = slotsOnSelectedDate.reduce((result: any, slot: any) => {
        const operatory = slot.operatory;
        if (!result[operatory]) {
          result[operatory] = [];
        }
        const startTimes = this.generateStartTimes(slot);
        result[operatory].push(...startTimes);
        
        // Sort slots by start time and remove duplicates
        if (result[operatory].length > 0) {
          result[operatory] = result[operatory]
            .sort((a: any, b: any) => a.start.getTime() - b.start.getTime())
            .filter((slot: any, index: number, array: any[]) => {
              if (index === 0) return true;
              // Remove slots that start before the previous slot ends
              return slot.start.getTime() >= array[index - 1].end.getTime();
            });
        }
        
        return result;
      }, {});
    }
  }

  generateStartTimes(slot: any): any[] {
    const startTimes: any[] = [];
    let startTime = new Date(slot.start);
    const endTime = new Date(slot.end);

    if (isBefore(endTime, addMinutes(startTime, this.appointmentDuration))) {
      return startTimes;
    }

    while (isBefore(startTime, endTime) || isEqual(startTime, endTime)) {
      const slotEndTime = addMinutes(startTime, this.appointmentDuration);
      if (isBefore(slotEndTime, endTime) || isEqual(slotEndTime, endTime)) {
        startTimes.push({
          start: new Date(startTime),
          end: slotEndTime,
          operatory: slot.operatory
        });
      }
      startTime = addMinutes(startTime, this.appointmentDuration);
      if (isAfter(startTime, endTime)) {
        break;
      }
    }
    return startTimes;
  }

  selectSlot(slot: any) {
    if (isValid(slot.start)) {
      // Toggle slot selection - deselect if same slot is clicked
      if (this.selectedSlot === slot) {
        this.selectedSlot = null;
        this.appointmentForm.patchValue({
          time: null,
          operatoryNumber: null
        });
      } else {
        this.selectedSlot = slot;
        this.appointmentForm.patchValue({
          time: format(slot.start, 'HH:mm'),
          operatoryNumber: slot.operatory
        });
      }
    }
  }

  gotoDashboard() {
    this.appState.setState({ appointmentData: null, appointmentBooked: false });
    this.router.navigate(['/dashboard', 'main']);
  }

  private checkAndShowOptionalForms(): void {
    const appointmentData = this.appState.getState().appointmentData;
    if (appointmentData && appointmentData.patientType === 'new-emergency') {
      // Navigate to dashboard first
      this.router.navigate(['/dashboard/main']).then(() => {
        // Show registration prompt after a short delay
        setTimeout(() => {
          this.showRegistrationPrompt();
        }, 500);
      });
    } else {
      this.gotoDashboard();
    }
  }

  private showRegistrationPrompt(): void {
    const dialogRef = this.dialog.open(RegistrationPromptComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'fill') {
        // User wants to fill registration form
        // Store a flag to indicate we should show consent after registration
        this.appState.setState({ showConsentAfterRegistration: true });
        this.router.navigate(['/dashboard/forms/new_patient_registration']);
      } else {
        // User skipped registration - add to pending forms
        const patientId = this.appState.getState().patientId || sessionStorage.getItem('patientId') || '';
        if (patientId) {
          const pendingFormsKey = `pending_forms_${patientId}`;
          const existingForms = JSON.parse(localStorage.getItem(pendingFormsKey) || '[]');
          if (!existingForms.includes('registration')) {
            existingForms.push('registration');
            localStorage.setItem(pendingFormsKey, JSON.stringify(existingForms));
          }
        }
        
        // Show consent form prompt
        this.showConsentPrompt();
      }
    });
  }

  showConsentPrompt(): void {
    const dialogRef = this.dialog.open(ConsentPromptComponent, {
      width: '500px',
      disableClose: false,
      panelClass: 'custom-dialog-container'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === 'fill') {
        // User wants to fill consent form
        this.router.navigate(['/dashboard/consent/informed_consent_general_dentistry']);
      } else {
        // User skipped consent - add to pending forms
        const patientId = this.appState.getState().patientId || sessionStorage.getItem('patientId') || '';
        if (patientId) {
          const pendingFormsKey = `pending_forms_${patientId}`;
          const existingForms = JSON.parse(localStorage.getItem(pendingFormsKey) || '[]');
          if (!existingForms.includes('consent')) {
            existingForms.push('consent');
            localStorage.setItem(pendingFormsKey, JSON.stringify(existingForms));
          }
        }
      }
      
      // Clear appointment data after the entire flow
      this.appState.setState({ appointmentData: null, appointmentBooked: false });
    });
  }

  getButtonText(): string {
    if (this.isConfirmationView) {
      return 'View My Appointments';
    }
    return 'Schedule Appointment';
  }

  getButtonAction() {
    if (this.isConfirmationView) {
      // Check if this is a new emergency patient
      const appointmentData = this.appState.getState().appointmentData;
      if (appointmentData && appointmentData.patientType === 'new-emergency') {
        // For new emergency patients, check and show optional forms
        this.checkAndShowOptionalForms();
      } else {
        // For other patients, go to dashboard
        this.gotoDashboard();
      }
    } else {
      this.saveAppointment();
    }
  }

  saveAppointment() {
    if (this.appointmentForm.valid) {
      const appointmentDate = this.appointmentForm.value.date;
      const appointmentTime = this.appointmentForm.value.time;
      const appointmentType = this.appointmentForm.value.appointmentType;
      const operatoryNumber = this.appointmentForm.value.operatoryNumber;
      const appointmentDescription = this.appointmentForm.value.appointmentDescription;
      let action = this.appointmentId ? 'reschedule' : 'schedule';
      if (this.currentAppointmentDetails && this.currentAppointmentDetails.appointmentStatus === 'Planned') {
        action = 'schedulePlanned';
      }
      // Cancelled appointments will use regular reschedule action with status field
      if (isValid(appointmentDate) && appointmentTime && appointmentType && operatoryNumber) {
        const appointmentDateTime = new Date(`${format(appointmentDate, 'yyyy-MM-dd')}T${appointmentTime}:00`);
        this.isScheduling = true;
        this.isGlobalLoading = true;
        this.isSubmitting = true;

        if (action === 'schedulePlanned') {
          const appointmentData = {
            appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
            appointmentNumber: this.appointmentId,
            operatoryNumber: operatoryNumber,
            providerNumber: this.currentAppointmentDetails.providerNumber,
          };
          this.appointmentService.schedulePlannedAppointment(appointmentData).pipe(takeUntil(this.unsubscribe$)).subscribe(
            {
              next: (response: any) => {
                this.isScheduling = false;
                this.isGlobalLoading = false;
                this.isSubmitting = false;
                if (response) {
                  this.snackBar.open('Planned appointment scheduled successfully', 'Close', { duration: 3000 });

                  // Set appointment data in state for confirmation view
                  const bookedAppointmentData = {
                    appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
                    isNewPatient: false,
                    appointmentTypeNumber: appointmentType,
                    summary: appointmentDescription,
                    operatoryNumber: operatoryNumber,
                    lengthInMinutes: this.selectedSlot?.end ?
                      Math.round((this.selectedSlot.end.getTime() - this.selectedSlot.start.getTime()) / 60000) : 30
                  };

                  this.appState.setState({
                    appointmentData: bookedAppointmentData,
                    appointmentBooked: true
                  });

                  // Show confirmation view
                  this.appointmentData = bookedAppointmentData;
                  this.isConfirmationView = true;
                }
              }, error: () => {
                this.isScheduling = false;
                this.isGlobalLoading = false;
                this.isSubmitting = false;
                this.snackBar.open('Error scheduling planned appointment. Please contact Clinic Support', 'Close', { duration: 3000 });
              }
            });
        } else if (action === 'reschedule') {
          const appointmentData: any = {
            appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
            appointmentNumber: this.appointmentId,
            appointmentType: appointmentType,
            operatoryNumber: operatoryNumber,
            action: action,
            clientId: this.clientId,
            patientId: this.patientId
          };
          
          // DEBUG: Log current appointment details to understand what we're working with
          
          // For cancelled appointments, create a new appointment instead of trying to reschedule
          if (this.currentAppointmentDetails && 
              (this.currentAppointmentDetails.appointmentStatus === 'Cancelled' || 
               this.currentAppointmentDetails.appointmentStatus === 'cancelled')) {
            
            
            // Create new appointment data for cancelled appointments with all required fields
            // Extract numeric ID from appointmentType (e.g., 'appointmenttypes/23' → 23)
            const appointmentTypeId = appointmentType.includes('/') ?
              parseInt(appointmentType.split('/')[1]) :
              parseInt(appointmentType);

            // Find the provider number from the selected operatory
            const selectedOperatory = this.operatories.find(op => op.operatoryNumber === parseInt(operatoryNumber));
            const providerNumberForCancelled = String(selectedOperatory?.providerNumber || '1');

            // Calculate length in minutes from selected slot or use default
            const lengthInMinutesForCancelled = this.selectedSlot?.end ?
              Math.round((this.selectedSlot.end.getTime() - this.selectedSlot.start.getTime()) / 60000) :
              this.appointmentDuration;

            const newAppointmentData: CreateAppointmentRequest = {
              appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
              appointmentTypeNumber: appointmentTypeId,
              operatoryNumber: parseInt(operatoryNumber),
              lengthInMinutes: lengthInMinutesForCancelled,
              providerNumber: providerNumberForCancelled,
              summary: `${appointmentDescription} (Rescheduled from cancelled appointment #${this.appointmentId})`,
              isNewPatient: false
            };
            
            
            this.appointmentService.createAppointment(newAppointmentData).pipe(takeUntil(this.unsubscribe$)).subscribe({
              next: (response: any) => {
                this.handleSuccessfulReschedule(appointmentDateTime, appointmentType, appointmentDescription, operatoryNumber, true);
              },
              error: (error) => {
                if (error.error && error.error.detail) {
                }
                this.handleRescheduleError('Unable to reschedule cancelled appointment. Please contact support.');
              }
            });
            return; // Exit early for cancelled appointments
          }
          
          // For regular appointments, use normal reschedule
          this.appointmentService.saveAppointment(appointmentData).pipe(takeUntil(this.unsubscribe$)).subscribe(
            {
              next: (response: any) => {
                if (response) {
                  this.handleSuccessfulReschedule(appointmentDateTime, appointmentType, appointmentDescription, operatoryNumber, false);
                }
              }, error: () => {
                this.handleRescheduleError('Error rescheduling appointment. Please contact Clinic Support');
              }
            });
        } else {
          // Check if patient is registered to determine patient type
          const patient = this.appState.getState().patient;
          const isNewPatient = !patient?.isRegistered;
          const patientType = isNewPatient ? 'new-emergency' : 'existing';

          // Find the provider number from the selected operatory
          const selectedOperatory = this.operatories.find(op => op.operatoryNumber === parseInt(operatoryNumber));
          // Ensure providerNumber is a string (backend expects string type)
          const providerNumber = String(selectedOperatory?.providerNumber || '1');

          // Calculate length in minutes from selected slot
          const lengthInMinutes = this.selectedSlot?.end ?
            Math.round((this.selectedSlot.end.getTime() - this.selectedSlot.start.getTime()) / 60000) :
            this.appointmentDuration;

          const appointmentData: CreateAppointmentRequest = {
            appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
            appointmentTypeNumber: parseInt(appointmentType),
            summary: appointmentDescription,
            operatoryNumber: parseInt(operatoryNumber),
            lengthInMinutes: lengthInMinutes,
            providerNumber: providerNumber,
            isNewPatient: isNewPatient
          };

          this.appointmentService.createAppointment(appointmentData).pipe(takeUntil(this.unsubscribe$)).subscribe(
            {
              next: (response: any) => {
                this.isScheduling = false;
                this.isGlobalLoading = false;
                this.isSubmitting = false;
                if (response) {
                  this.snackBar.open('Appointment scheduled successfully', 'Close', { duration: 3000 });

                  // Set appointment data in state for confirmation view
                  const bookedAppointmentData = {
                    appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
                    isNewPatient: isNewPatient,
                    appointmentTypeNumber: appointmentType,
                    summary: appointmentDescription,
                    operatoryNumber: operatoryNumber,
                    lengthInMinutes: this.selectedSlot?.end ?
                      Math.round((this.selectedSlot.end.getTime() - this.selectedSlot.start.getTime()) / 60000) : 30,
                    patientType: patientType
                  };

                  this.appState.setState({
                    appointmentData: bookedAppointmentData,
                    appointmentBooked: true
                  });

                  // Show confirmation view
                  this.appointmentData = bookedAppointmentData;
                  this.isConfirmationView = true;
                }
              }, error: () => {
                this.isScheduling = false;
                this.isGlobalLoading = false;
                this.isSubmitting = false;
                this.snackBar.open('Error scheduling appointment. Please try again.', 'Close', { duration: 3000 });
              }
            });
        }
      }
    }
  }

  formatMonthKey(date: Date | null): string {
    if (!date) {
      return '';
    }
    return format(date, 'yyyy-MM');
  }

  isMonthLoading(): boolean {
    const monthKey = this.currentViewedMonth;
    return this.calendarLoadingMonths[monthKey] || false;
  }

  onMonthSelected(event: Date) {
    const selectedMonth = startOfMonth(event);
    const monthKey = this.formatMonthKey(selectedMonth);
    this.currentViewedMonth = monthKey;
    this.selectedDate = null;
    const operatories = this.operatories.map(operatory => operatory.operatoryNumber).join(',')
    if (!this.slotCacheService.getCachedSlots(monthKey, operatories)) {
      this.isDatepickerLoading = true;
      this.loadAvailableSlotsForMonth(selectedMonth, monthKey, operatories);
    } else {
      this.availableSlots = this.slotCacheService.getCachedSlots(monthKey, operatories) || [];
      this.displayFirstAvailableDaySlots();
    }
  }


  dateChanged(event: any) {
    if (event) {
      this.selectedDate = new Date(event);
      this.appointmentForm.patchValue({ date: this.selectedDate });
      this.updateDisplayedSlots(this.selectedDate);
    } else {
      // Date deselected
      this.selectedDate = null;
      this.appointmentForm.patchValue({ date: null });
      this.displayedSlots = {};
    }
    // Always clear selected slot when date changes or is deselected
    this.selectedSlot = null;
    this.appointmentForm.patchValue({ time: null, operatoryNumber: null });
  }

  dateClass: MatCalendarCellClassFunction<Date> = (cellDate, view): string => {
    if (view === 'month') {
      if (isValid(cellDate)) {
        const formattedDate = format(cellDate, 'yyyy-MM-dd');
        const isAvailable = this.availableSlots.some(slot => format(slot.date, 'yyyy-MM-dd') === formattedDate);
        return isAvailable ? 'available-slot' : '';
      }
    }
    return '';
  }

  getObjectKeys(obj: any): string[] {
    if (!obj) {
      return [];
    }
    return Object.keys(obj);
  }

  generateTimeOptions(slot: any): string[] {
    const options: string[] = [];
    let startTime = new Date(slot.start);
    const endTime = new Date(slot.end);
    while (isBefore(startTime, endTime)) {
      options.push(format(startTime, 'HH:mm'));
      startTime = addMinutes(startTime, 30);
    }
    return options;
  }

  getActionText(): string {
    if (this.isConfirmationView) {
      // For new emergency patients, show "Continue" to proceed with optional forms
      const appointmentData = this.appState.getState().appointmentData;
      if (appointmentData && appointmentData.patientType === 'new-emergency') {
        return 'Continue';
      }
      return 'View My Appointments';
    }
    if (this.currentAppointmentDetails && this.currentAppointmentDetails.appointmentStatus === 'Planned') {
      return 'Schedule Planned';
    }
    if (this.currentAppointmentDetails && this.currentAppointmentDetails.appointmentStatus === 'Scheduled') {
      return 'Reschedule Appointment';
    }
    if (this.appointmentId && this.isLoading) {
      return this.loadingStatus;
    }
    return 'Schedule Appointment';
  }

  getStatusIcon(status: string): string {
    if (!status) {
      return 'help';
    }
    const lowerStatus = status.toLowerCase();
    const icons: any = {
      scheduled: 'event_available',
      broken: 'event_busy',
      complete: 'check_circle',
      completed: 'check_circle',
      confirmed: 'event_available',
      cancelled: 'event_busy'
    };
    return icons[lowerStatus] || 'help';
  }

  getStatusColor(status: string): string {
    if (!status) {
      return 'default-color';
    }
    const lowerStatus = status.toLowerCase();
    const colors: any = {
      scheduled: 'primary',
      broken: 'warn',
      unschedlist: 'warn',
      planned: 'accent',
      complete: 'completed',
      completed: 'completed',
      confirmed: 'primary',
      cancelled: 'warn'
    };
    return colors[lowerStatus] || 'default-color';
  }

  getStatusDisplay(status: string): string {
    if (!status) {
      return 'Unknown';
    }
    const lowerStatus = status.toLowerCase();
    const statuses: any = {
      scheduled: 'Scheduled',
      broken: 'Missed',
      unschedlist: 'Cancelled',
      complete: 'Complete',
      completed: 'Completed',
      planned: 'Planned',
      confirmed: 'Confirmed',
      cancelled: 'Cancelled'
    };
    return statuses[lowerStatus] || 'Unknown';
  }

  formatAppointmentDate(date: string): string {
    const dateFormatted = formatDate(date, 'shortDate', 'en-US') || '';
    const timeFormatted = formatDate(date, 'shortTime', 'en-US') || '';
    return `${dateFormatted} at ${timeFormatted}`;
  }

  private handleSuccessfulReschedule(appointmentDateTime: Date, appointmentType: any, appointmentDescription: any, operatoryNumber: any, isCancelled: boolean = false): void {
    this.isScheduling = false;
    this.isGlobalLoading = false;
    this.isSubmitting = false;
    
    const successMessage = isCancelled ? 
      'Cancelled appointment reactivated and rescheduled successfully' : 
      'Appointment rescheduled successfully';
    this.snackBar.open(successMessage, 'Close', { duration: 3000 });

    // Set appointment data in state for confirmation view
    const bookedAppointmentData = {
      appointmentDateTime: format(appointmentDateTime, 'yyyy-MM-dd HH:mm:ss'),
      isNewPatient: false,
      appointmentTypeNumber: appointmentType,
      summary: appointmentDescription,
      operatoryNumber: operatoryNumber,
      lengthInMinutes: this.selectedSlot?.end ?
        Math.round((this.selectedSlot.end.getTime() - this.selectedSlot.start.getTime()) / 60000) : 30
    };

    this.appState.setState({
      appointmentData: bookedAppointmentData,
      appointmentBooked: true
    });

    // Show confirmation view
    this.appointmentData = bookedAppointmentData;
    this.isConfirmationView = true;
  }

  private handleRescheduleError(message: string): void {
    this.isScheduling = false;
    this.isGlobalLoading = false;
    this.isSubmitting = false;
    this.snackBar.open(message, 'Close', { duration: 3000 });
  }

}